import { GetServerSideProps, NextPage } from 'next'
import nextI18nextConfig from '~/next-i18next.config'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { Faq } from '~/components/Faq/Faq'
import { GetLatestTrophies } from '~/modules/Organizers/Application/GetLatestTrophies'
import { DependencyInjector } from '~/injector/dependency.injector'
import { GetFeaturedTrophy } from '~/modules/Trophies/Application/GetFeaturedTrophy/GetFeaturedTrophy'
import { ApiTrophyResponseInterface } from '~/modules/Trophies/Application/ApiTrophyResponseInterface'
import { OrganizerFeaturedTrophy } from '~/components/OrganizerFeaturedTrophy/OrganizerFeaturedTrophy'
import { HtmlPageMeta } from '~/components/HtmlPageMeta/HtmlPageMeta'
import { HtmlPageMetaContextProps } from '~/components/HtmlPageMeta/HtmlPageMetaContextProps'
import { HtmlPageMetaContextService } from '~/components/HtmlPageMeta/HtmlPageMetaContextService'
import { HtmlPageMetaResourceService } from '~/components/HtmlPageMeta/HtmlPageMetaResourceService'
import { GetOrganizerResponseInterface }
  from '~/modules/Organizers/Application/GetOrganizer/GetOrganizerResponseInterface'
import { GetOrganizerResponseTranslator }
  from '~/modules/Organizers/Application/GetOrganizer/GetOrganizerResponseTranslator'
import { TrophyRepositoryInterface } from '~/modules/Trophies/Domain/TrophyRepositoryInterface'
import { OrganizerRepositoryInterface } from '~/modules/Organizers/Domain/OrganizerRepositoryInterface'
import ApiTrophyResponseTranslator from '~/modules/Trophies/Application/ApiTrophyResponseTranslator'
import { GetOrganizer } from '~/modules/Organizers/Application/GetOrganizer/GetOrganizer'
import { OrganizerHeader } from '~/components/OrganizerHeader/OrganizerHeader'
import { OrganizerLatestTrophies } from '~/components/OrganizerLatestTrophies/OrganizerLatestTrophies'
import { ApplicationTrophyDto } from '~/modules/Products/Application/Dtos/ApplicationTrophyDto'
import { ComponentTrophyDtoTransformer } from '~/modules/Products/Infrastructure/ComponentTrophyDtoTransformer'

interface Props {
  htmlPageMetaContextProps: HtmlPageMetaContextProps
  trophies: ApplicationTrophyDto[]
  featuredTrophy: ApiTrophyResponseInterface | null
  organizerResponse: GetOrganizerResponseInterface
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const locale = context.locale ? context.locale : nextI18nextConfig.i18n.defaultLocale

  const organizerId = context.query.organizerId as string
  const trophyRepository: TrophyRepositoryInterface = DependencyInjector.getInstance()
    .resolve('TrophyRepositoryInterface')
  const organizerRepository: OrganizerRepositoryInterface = DependencyInjector.getInstance()
    .resolve('OrganizerRepositoryInterface')

  let featuredTrophy = null

  if (organizerId) {
    try {
      featuredTrophy = await (new GetFeaturedTrophy(trophyRepository)).run(organizerId)
    } catch (exception: unknown) {
      console.error(exception)
    }
  }

  try {
    const organizerResponse = await (new GetOrganizer(organizerRepository, trophyRepository)).get(organizerId)
    const trophies = await (new GetLatestTrophies(organizerRepository, trophyRepository)).get(organizerId)
    const htmlPageMetaContextService = new HtmlPageMetaContextService(context)

    return {
      props: {
        featuredTrophy: JSON.parse(JSON.stringify(featuredTrophy)),
        organizerResponse,
        trophies: JSON.parse(JSON.stringify(trophies)),
        htmlPageMetaContextProps: htmlPageMetaContextService.getProperties(),
        ...await serverSideTranslations(locale, [
          'user_tos',
          'app_menu',
          'footer',
          'faq',
          'latest_trophies',
          'organizer',
          'organizer_header',
          'featured_trophy',
          'wallet_error_modal',
        ]),
      },
    }
  } catch (exception: unknown) {
    // Log error, we don't want to throw exceptions to our own frontend
    console.error(exception)

    return {
      notFound: true,
    }
  }
}

const OrganizerPage: NextPage<Props> = (props) => {
  const { t } = useTranslation('organizer')
  const { organizer, trophyAmount } = GetOrganizerResponseTranslator.domainFromResponse(props.organizerResponse)
  let organizerFeaturedTrophy
  let organizerLatestTrophies

  if (props.featuredTrophy !== null) {
    const trophy = ApiTrophyResponseTranslator.domainFromResponse(props.featuredTrophy)

    organizerFeaturedTrophy = (<OrganizerFeaturedTrophy trophy={ trophy } />)
  }

  if (props.trophies.length > 0) {
    const latestTrophies = props.trophies.map((trophy) =>
      ComponentTrophyDtoTransformer.fromApplication(trophy)
    )

    organizerLatestTrophies = (<OrganizerLatestTrophies trophies={ latestTrophies } />)
  }

  const htmlPageMetaUrlProps = (
    new HtmlPageMetaResourceService(organizer.name, t('organizer_page_description'))
  ).getProperties()
  const htmlPageMetaProps = { ...props.htmlPageMetaContextProps, ...htmlPageMetaUrlProps }

  return (
    <>
      <HtmlPageMeta { ...htmlPageMetaProps } />

      <OrganizerHeader
        organizer={ organizer }
        trophyAmount={ trophyAmount }
      />

      { organizerFeaturedTrophy }

      { organizerLatestTrophies }

      <Faq />
    </>
  )
}

export default OrganizerPage
